import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useAuth } from '../contexts/AuthContext';

const ContactModal = ({ show, onHide }) => {
  const { user } = useAuth();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (!subject.trim()) {
      setError('Subject is required');
      return;
    }

    if (!body.trim()) {
      setError('Message is required');
      return;
    }

    if (subject.length > 200) {
      setError('Subject must be 200 characters or less');
      return;
    }

    if (body.length > 2000) {
      setError('Message must be 2000 characters or less');
      return;
    }

    setSending(true);
    setError(null);

    try {
      const response = await fetch('/api/contact/send', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subject: subject.trim(),
          body: body.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }

      setSuccess(true);
      setSubject('');
      setBody('');

      // Close modal after 2 seconds
      setTimeout(() => {
        setSuccess(false);
        onHide();
      }, 2000);

    } catch (err) {
      console.error('Contact form error:', err);
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!sending) {
      setSubject('');
      setBody('');
      setError(null);
      setSuccess(false);
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header
        closeButton
        style={{
          background: '#1e293b',
          color: '#fff',
          borderBottom: '1px solid #334155'
        }}
      >
        <Modal.Title style={{ fontSize: '20px', fontWeight: '600' }}>
          💬 Contact Us
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ background: '#0f172a', color: '#cbd5e1', padding: '24px' }}>
        {success ? (
          <div style={{
            background: '#14532d',
            border: '1px solid #22c55e',
            borderRadius: '8px',
            padding: '16px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>✓</div>
            <div style={{ color: '#86efac', fontSize: '14px' }}>
              Message sent successfully! We'll respond as soon as possible.
            </div>
          </div>
        ) : (
          <>
            {/* Whop Support Chat Recommendation */}
            <div style={{
              background: 'rgba(255, 98, 67, 0.1)',
              border: '1px solid #334155',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <div style={{ fontSize: '15px', color: '#fff', marginBottom: '10px', fontWeight: '600' }}>
                💬 Preferred: Use Whop Support Chat
              </div>
              <div style={{ fontSize: '13px', color: '#cbd5e1', lineHeight: '1.6', marginBottom: '12px' }}>
                To send a message in the Whop platform (preferred),{' '}
                <a
                  href="https://whop.com/joined/simpleodds/support/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#FF6243',
                    fontWeight: '600',
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px'
                  }}
                >
                  click here to open Whop Support
                </a>
                , login to Whop (if necessary), and click <strong>{user?.name || 'your name'} Support Chat</strong>.
              </div>
              <div style={{
                fontSize: '12px',
                color: '#94a3b8',
                fontStyle: 'italic',
                paddingTop: '8px',
                borderTop: '1px solid #334155'
              }}>
                Alternatively, submit a message via the form below and we will respond via email.
              </div>
            </div>

            <Form onSubmit={handleSubmit}>
            {error && (
              <div style={{
                background: '#7f1d1d',
                border: '1px solid #ef4444',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <div style={{ color: '#fca5a5', fontSize: '13px' }}>
                  ⚠️ {error}
                </div>
              </div>
            )}

            {/* From field - display only */}
            <div className="mb-3">
              <div style={{ color: '#cbd5e1', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                From:
              </div>
              <div style={{
                background: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '6px',
                padding: '10px 12px',
                color: '#94a3b8',
                fontSize: '14px'
              }}>
                {user?.email || 'Not logged in'}
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label style={{ color: '#cbd5e1', fontSize: '14px', fontWeight: '600' }}>
                Subject
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="What's this about?"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
                disabled={sending}
                style={{
                  background: '#1e293b',
                  border: '1px solid #334155',
                  color: '#fff',
                  fontSize: '14px'
                }}
              />
              <Form.Text style={{ color: '#64748b', fontSize: '12px' }}>
                {subject.length}/200 characters
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ color: '#cbd5e1', fontSize: '14px', fontWeight: '600' }}>
                Message
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                placeholder="How can we help you?"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={2000}
                disabled={sending}
                style={{
                  background: '#1e293b',
                  border: '1px solid #334155',
                  color: '#fff',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
              <Form.Text style={{ color: '#64748b', fontSize: '12px' }}>
                {body.length}/2000 characters
              </Form.Text>
            </Form.Group>

            <div className="d-flex gap-2 justify-content-end">
              <Button
                variant="outline-secondary"
                onClick={handleClose}
                disabled={sending}
                style={{
                  fontSize: '14px',
                  padding: '8px 16px'
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sending}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  padding: '8px 20px'
                }}
              >
                {sending ? 'Sending...' : 'Send Message'}
              </Button>
            </div>
          </Form>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ContactModal;
